import { useApp } from "../context/AppContext";
import { tr, pick } from "../i18n/translations";
import { CalendarDays, Quote, Sparkles, BookOpenText } from "lucide-react";

function daysToBackToSchool() {
  const today = new Date();
  let year = today.getFullYear();
  const back = new Date(year, 8, 1); // Sept 1
  if (today > back) back.setFullYear(year + 1);
  return Math.ceil((back - today) / (1000 * 60 * 60 * 24));
}

export default function PedagogicalWidget({ content }) {
  const { lang } = useApp();
  // Prefer the new single-pick fields (word/quote/stat) computed by the backend with daily/weekly rotation.
  // Fallback to legacy arrays for safety.
  const word = content?.word?.fr || content?.words_of_day?.[0]?.fr;
  const quote = content?.quote || content?.quotes?.[0];
  const stat = content?.stat || content?.dys_stats?.[0];
  const rotation = content?.rotation;
  const days = daysToBackToSchool();
  const statText = pick(stat, "fr") || "8 % des enfants présentent un trouble DYS.";
  // Extract the first numeric token (handles "8 %", "86 milliards", "1/10", "5,9 %", "10 à 11 h", "25 ans")
  const statHighlight = (() => {
    const m = statText.match(/(\d[\d\s,.]*\s*(?:%|milliards?|millions?|h|ans?|fois|sur\s+\d+|\/\s*\d+)?)/i);
    return m ? m[1].trim() : statText.split(" ").slice(0, 2).join(" ");
  })();

  return (
    <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16" data-testid="pedagogical-widget">
      <div className="mb-10 max-w-2xl">
        <p className="text-xs uppercase tracking-[0.25em] text-ink/60 mb-3">Le widget pédagogique</p>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-light tracking-tight">
          La culture orthophonique, <span className="italic text-pedablue">chaque jour</span>.
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        <div className="rounded-3xl bg-white border border-ink/10 p-7 md:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-pedablue">
              <BookOpenText size={18} />
              <span className="text-xs uppercase tracking-widest">{tr(lang, "widget_word")}</span>
            </div>
            {rotation && <span className="text-[10px] uppercase tracking-widest text-ink-soft/70">Jour {rotation.day_of_year}</span>}
          </div>
          <p className="font-display text-3xl italic mb-3">{word?.word || "Phonème"}</p>
          <p className="text-sm text-ink-soft leading-relaxed">{word?.definition || "Plus petite unité sonore distinctive d'une langue."}</p>
        </div>

        <div className="rounded-3xl bg-sage/30 border border-sage/40 p-7">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-pedablue">
              <Quote size={18} />
              <span className="text-xs uppercase tracking-widest">{tr(lang, "widget_quote")}</span>
            </div>
            {rotation && <span className="text-[10px] uppercase tracking-widest text-ink-soft/70">Jour {rotation.day_of_year}</span>}
          </div>
          <p className="font-display text-lg italic leading-snug">{pick(quote, "fr") || "« L'enfant est un feu qu'on allume. »"}</p>
        </div>

        <div className="rounded-3xl bg-coral/15 border border-coral/30 p-7">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-coral-dark">
              <Sparkles size={18} />
              <span className="text-xs uppercase tracking-widest">{tr(lang, "widget_stat")}</span>
            </div>
            {rotation && <span className="text-[10px] uppercase tracking-widest text-coral-dark/70">Jour {rotation.day_of_year}</span>}
          </div>
          <p className="font-display text-3xl font-light">{statHighlight}</p>
          <p className="text-sm text-ink-soft mt-2">{statText}</p>
        </div>

        <div className="rounded-3xl bg-ink text-ivory p-7 md:col-span-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <CalendarDays size={20} />
              <span className="text-xs uppercase tracking-widest text-ivory/70">{tr(lang, "widget_back")}</span>
            </div>
            <p className="font-display text-3xl md:text-4xl">
              <span className="italic font-light">J–{days}</span> avant la rentrée scolaire
            </p>
            <a href="/catalogue?promo=true" className="text-sm underline underline-offset-4">Préparer la rentrée →</a>
          </div>
        </div>
      </div>
    </section>
  );
}
